import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Calendar,
  Clock,
  User,
  Mail,
  XCircle,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Import axios instance
import { axiosInstance } from "../../../utils/axios.js";

function ManageBooking() {
  const { token } = useParams();

  const [booking, setBooking] = useState(null);
  const [eventType, setEventType] = useState(null);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [existingBookings, setExistingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // UI states
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCancelSeriesConfirm, setShowCancelSeriesConfirm] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Calendar state
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [timeFormat] = useState("12h");

  // Fetch booking data on mount
  useEffect(() => {
    if (token) {
      fetchBookingData();
    }
  }, [token]);

  // Fetch existing bookings when date changes (for reschedule conflict checking)
  useEffect(() => {
    if (availabilityData && selectedDate && eventType?.id) {
      fetchExistingBookingsForDate(selectedDate);
    }
  }, [selectedDate, availabilityData, eventType?.id]);

  // Update available slots when existingBookings updates (after fetching)
  useEffect(() => {
    if (availabilityData && selectedDate) {
      const slots = getAvailableSlots(
        availabilityData,
        selectedDate,
        eventType?.duration || 30
      );
      setAvailableSlots(slots);
      setSelectedTime(null);
    }
  }, [existingBookings, selectedDate, availabilityData, eventType?.duration]);

  const fetchBookingData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetPublicBooking($token: String!) {
            getPublicBooking(token: $token) {
              booking {
                id
                eventTypeId
                date
                timeStart
                timeEnd
                name
                email
                notes
                timezone
                status
                assignedHostName
                createdAt
                recurringSeriesId
                recurringIndex
                totalInSeries
              }
              eventType {
                id
                title
                description
                duration
              }
              availabilityData {
                id
                name
                days
                timeSlots {
                  timeStart
                  timeEnd
                }
                dayTimeSlots {
                  day
                  slots {
                    timeStart
                    timeEnd
                  }
                }
                overrides {
                  date
                  isUnavailable
                  timeSlots {
                    timeStart
                    timeEnd
                  }
                }
              }
              existingBookings {
                id
                date
                timeStart
                timeEnd
              }
            }
          }
        `,
        variables: { token },
      });

      const result =
        response.data?.data?.getPublicBooking ||
        response.data?.getPublicBooking;

      if (result) {
        setBooking(result.booking);
        setEventType(result.eventType);
        setAvailabilityData(result.availabilityData);
        setExistingBookings(result.existingBookings || []);
      } else if (response.data?.errors) {
        setError(response.data.errors[0]?.message || "Failed to load booking");
      }
    } catch (err) {
      console.error("Error fetching booking:", err);
      setError(
        err.message ||
          "Failed to load booking. The link may be invalid or expired."
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch existing bookings for a specific date (for filtering available slots)
  const fetchExistingBookingsForDate = async (date) => {
    if (!eventType?.id) return;

    try {
      const formattedDate = dayjs(date).format("YYYY-MM-DD");
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetExistingBookings($eventTypeId: ID!, $date: String!) {
            getBookingsForEventAndDate(eventTypeId: $eventTypeId, date: $date) {
              id
              date
              timeStart
              timeEnd
              status
            }
          }
        `,
        variables: {
          eventTypeId: eventType.id,
          date: formattedDate,
        },
      });

      if (response?.data?.data?.getBookingsForEventAndDate) {
        const allBookings = response.data.data.getBookingsForEventAndDate;
        // Filter out canceled/rejected bookings and exclude current booking
        const activeBookings = allBookings.filter((b) => {
          const status = b.status?.toLowerCase();
          if (
            status === "canceled" ||
            status === "cancelled" ||
            status === "rejected"
          ) {
            return false;
          }
          // Exclude current booking being rescheduled
          if (b.id === booking?.id) {
            return false;
          }
          return true;
        });
        console.log("Filtered active bookings for reschedule:", activeBookings);
        setExistingBookings(activeBookings);
      } else if (response?.data?.getBookingsForEventAndDate) {
        const allBookings = response.data.getBookingsForEventAndDate;
        const activeBookings = allBookings.filter((b) => {
          const status = b.status?.toLowerCase();
          if (
            status === "canceled" ||
            status === "cancelled" ||
            status === "rejected"
          ) {
            return false;
          }
          if (b.id === booking?.id) {
            return false;
          }
          return true;
        });
        console.log("Filtered active bookings for reschedule:", activeBookings);
        setExistingBookings(activeBookings);
      }
    } catch (error) {
      console.error("Error fetching existing bookings:", error);
    }
  };

  // Generate time slots based on availability
  const generateTimeSlots = (timeStart, timeEnd, interval = 30) => {
    const slots = [];
    let startTime, endTime;

    // Parse time strings
    if (
      typeof timeStart === "string" &&
      (timeStart.includes("am") || timeStart.includes("pm"))
    ) {
      const [timePart, meridiem] = timeStart.toLowerCase().split(/\s*(am|pm)/);
      const [hours, minutes] = timePart.split(":").map(Number);
      let hour24 = hours;
      if (meridiem === "pm" && hours < 12) hour24 = hours + 12;
      else if (meridiem === "am" && hours === 12) hour24 = 0;
      startTime = dayjs()
        .hour(hour24)
        .minute(minutes || 0)
        .second(0);
    } else {
      startTime = dayjs(`2000-01-01 ${timeStart}`);
    }

    if (
      typeof timeEnd === "string" &&
      (timeEnd.includes("am") || timeEnd.includes("pm"))
    ) {
      const [timePart, meridiem] = timeEnd.toLowerCase().split(/\s*(am|pm)/);
      const [hours, minutes] = timePart.split(":").map(Number);
      let hour24 = hours;
      if (meridiem === "pm" && hours < 12) hour24 = hours + 12;
      else if (meridiem === "am" && hours === 12) hour24 = 0;
      endTime = dayjs()
        .hour(hour24)
        .minute(minutes || 0)
        .second(0);
    } else {
      endTime = dayjs(`2000-01-01 ${timeEnd}`);
    }

    let currentTime = startTime.clone();
    while (currentTime.isBefore(endTime)) {
      const slotEnd = currentTime.add(interval, "minute");
      if (slotEnd.isAfter(endTime)) break;

      slots.push({
        timeStart: currentTime.format(timeFormat === "12h" ? "h:mma" : "HH:mm"),
        timeEnd: slotEnd.format(timeFormat === "12h" ? "h:mma" : "HH:mm"),
        timeStart24: currentTime.format("HH:mm"),
        timeEnd24: slotEnd.format("HH:mm"),
      });
      currentTime = currentTime.add(interval, "minute");
    }

    return slots;
  };

  // Get available slots for a selected date
  const getAvailableSlots = (availability, date, duration) => {
    if (!availability || !availability.days) return [];

    const dayOfWeek = date.format("dddd");
    const selectedDateStr = date.format("YYYY-MM-DD");
    const today = dayjs().startOf("day");
    const now = dayjs();

    // Check if date is in the past
    if (date.startOf("day").isBefore(today)) return [];

    // Check for overrides
    const override = availability.overrides?.find(
      (o) => dayjs(o.date).format("YYYY-MM-DD") === selectedDateStr
    );

    let timeSlots = [];

    if (override) {
      if (override.isUnavailable) return [];
      if (override.timeSlots?.length > 0) {
        override.timeSlots.forEach((slot) => {
          const slots = generateTimeSlots(
            slot.timeStart,
            slot.timeEnd,
            duration
          );
          timeSlots = [...timeSlots, ...slots];
        });
      }
    } else if (availability.days?.includes(dayOfWeek)) {
      // Check dayTimeSlots first (new structure)
      const dayTimeSlotEntry = availability.dayTimeSlots?.find(
        (dts) => dts.day === dayOfWeek
      );

      if (dayTimeSlotEntry && dayTimeSlotEntry.slots?.length > 0) {
        dayTimeSlotEntry.slots.forEach((slot) => {
          const slots = generateTimeSlots(
            slot.timeStart,
            slot.timeEnd,
            duration
          );
          timeSlots = [...timeSlots, ...slots];
        });
      } else if (availability.timeSlots?.length > 0) {
        // Fallback to legacy timeSlots
        availability.timeSlots.forEach((slot) => {
          const slots = generateTimeSlots(
            slot.timeStart,
            slot.timeEnd,
            duration
          );
          timeSlots = [...timeSlots, ...slots];
        });
      }
    }

    // Filter for minimum notice (if today, exclude past times)
    if (date.isSame(today, "day")) {
      timeSlots = timeSlots.filter((slot) => {
        const [hours, minutes] = slot.timeStart24.split(":").map(Number);
        const slotTime = date.hour(hours).minute(minutes);
        return slotTime.isAfter(now.add(30, "minute")); // 30 min notice
      });
    }

    // Helper to convert any time format to normalized 12h format
    const convertTo12h = (timeStr) => {
      if (!timeStr) return "";
      // If already 12h format (contains am/pm), just normalize
      if (
        timeStr.toLowerCase().includes("am") ||
        timeStr.toLowerCase().includes("pm")
      ) {
        return timeStr.replace(/\s+/g, "").toLowerCase();
      }
      // Convert 24h to 12h using dayjs (handles "22:30" -> "10:30pm")
      return dayjs(`2000-01-01 ${timeStr}`).format("h:mma").toLowerCase();
    };

    // Filter out already booked slots (ported from Preview.js)
    if (existingBookings && existingBookings.length > 0) {
      timeSlots = timeSlots.filter((slot) => {
        // Convert slot time to normalized 12h format
        const slotTime12h = convertTo12h(slot.timeStart);

        // Check if any booking matches this slot
        const isBooked = existingBookings.some((existingBooking) => {
          if (existingBooking.date !== selectedDateStr) return false;
          // Convert booking time to 12h format too (handles mixed formats in DB)
          const bookingTime12h = convertTo12h(existingBooking.timeStart);
          return bookingTime12h === slotTime12h;
        });
        return !isBooked;
      });
    }

    // Also hide current booking's time slot on same date (prevent accidental same-time reschedule)
    if (booking && booking.date === selectedDateStr) {
      const currentBookingTime = convertTo12h(booking.timeStart);
      timeSlots = timeSlots.filter((slot) => {
        const slotTime12h = convertTo12h(slot.timeStart);
        return slotTime12h !== currentBookingTime;
      });
    }

    return timeSlots;
  };

  // Check if a day is available
  const isDayAvailable = (date) => {
    if (!availabilityData) return false;
    const dayOfWeek = date.format("dddd");
    const today = dayjs().startOf("day");

    // Past dates are not available
    if (date.startOf("day").isBefore(today)) return false;

    // Check for overrides
    const override = availabilityData.overrides?.find(
      (o) => dayjs(o.date).format("YYYY-MM-DD") === date.format("YYYY-MM-DD")
    );
    if (override) return !override.isUnavailable;

    // Check if day is in available days
    return availabilityData.days?.includes(dayOfWeek);
  };

  // Calendar navigation
  const prevMonth = () => {
    setSelectedDate(selectedDate.subtract(1, "month"));
  };

  const nextMonth = () => {
    setSelectedDate(selectedDate.add(1, "month"));
  };

  const canGoPrevMonth = () => {
    const today = dayjs().startOf("month");
    return (
      selectedDate.startOf("month").isAfter(today) ||
      selectedDate.startOf("month").isSame(today)
    );
  };

  // Render month calendar view
  const renderMonthView = () => {
    const daysInMonth = selectedDate.daysInMonth();
    const firstDay = selectedDate.startOf("month").day();
    const days = [];
    const weekdays = ["S", "M", "T", "W", "T", "F", "S"];
    const today = dayjs().startOf("day");

    // Create days for current month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = selectedDate.date(i);
      const isToday =
        date.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD");
      const isSelected =
        date.format("YYYY-MM-DD") === selectedDate.format("YYYY-MM-DD");
      const dayAvailable = isDayAvailable(date);
      const isPast = date.startOf("day").isBefore(today);

      days.push({
        date: i,
        isCurrentMonth: true,
        isToday,
        isSelected,
        isAvailable: dayAvailable && !isPast,
        isPast,
        fullDate: date,
      });
    }

    // Add empty cells before first day
    const emptyCellsBefore = [];
    for (let i = 0; i < firstDay; i++) {
      emptyCellsBefore.push({ date: null, isCurrentMonth: false });
    }

    const allDays = [...emptyCellsBefore, ...days];

    return (
      <div style={styles.calendarContainer}>
        <div style={styles.monthHeader}>
          <button
            style={styles.navButton}
            onClick={prevMonth}
            disabled={!canGoPrevMonth()}
          >
            <ChevronLeft size={20} />
          </button>
          <h3 style={styles.monthTitle}>{selectedDate.format("MMMM YYYY")}</h3>
          <button style={styles.navButton} onClick={nextMonth}>
            <ChevronRight size={20} />
          </button>
        </div>

        <div style={styles.weekdaysGrid}>
          {weekdays.map((day, index) => (
            <div key={index} style={styles.weekday}>
              {day}
            </div>
          ))}
        </div>

        <div style={styles.daysGrid}>
          {allDays.map((day, index) => (
            <div key={index} style={styles.dayCell}>
              {day.date && (
                <button
                  style={{
                    ...styles.dayButton,
                    ...(day.isSelected ? styles.dayButtonSelected : {}),
                    ...(day.isToday && !day.isSelected
                      ? styles.dayButtonToday
                      : {}),
                    ...(!day.isAvailable ? styles.dayButtonUnavailable : {}),
                  }}
                  onClick={() =>
                    day.isAvailable && setSelectedDate(day.fullDate)
                  }
                  disabled={!day.isAvailable}
                >
                  {day.date}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render time slots
  const renderTimeSlots = () => {
    return (
      <div style={styles.timeSlotsContainer}>
        <h3 style={styles.timeSlotsTitle}>
          <Clock size={18} style={{ marginRight: "8px" }} />
          Available Times
        </h3>
        <p style={styles.selectedDateText}>
          {selectedDate.format("dddd, MMMM D, YYYY")}
        </p>

        {availableSlots.length > 0 ? (
          <div style={styles.timeSlotsGrid}>
            {availableSlots.map((slot, index) => (
              <button
                key={index}
                style={{
                  ...styles.timeSlotButton,
                  ...(selectedTime?.timeStart === slot.timeStart
                    ? styles.timeSlotButtonSelected
                    : {}),
                }}
                onClick={() => setSelectedTime(slot)}
              >
                {slot.timeStart}
              </button>
            ))}
          </div>
        ) : (
          <div style={styles.noSlotsMessage}>
            <p>No available time slots for this date.</p>
            <p style={{ fontSize: "13px", color: "#888" }}>
              Try selecting a different date.
            </p>
          </div>
        )}
      </div>
    );
  };

  const handleCancel = async () => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation CancelPublicBooking($token: String!) {
            cancelPublicBooking(token: $token) {
              success
              message
              booking {
                id
                status
              }
            }
          }
        `,
        variables: { token },
      });

      const result =
        response.data?.data?.cancelPublicBooking ||
        response.data?.cancelPublicBooking;

      if (result?.success) {
        setSuccess("Your booking has been canceled successfully.");
        setShowCancelConfirm(false);
        fetchBookingData();
      } else {
        setError(result?.message || "Failed to cancel booking");
      }
    } catch (err) {
      console.error("Error canceling booking:", err);
      setError(err.message || "Failed to cancel booking");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel entire series
  const handleCancelSeries = async () => {
    if (!booking?.recurringSeriesId) {
      setError("No recurring series found");
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation CancelRecurringSeries($seriesId: String!) {
            cancelRecurringSeries(seriesId: $seriesId) {
              success
              message
              bookings {
                id
                status
              }
            }
          }
        `,
        variables: { seriesId: booking.recurringSeriesId },
      });

      const result =
        response.data?.data?.cancelRecurringSeries ||
        response.data?.cancelRecurringSeries;

      if (result?.success) {
        const cancelledCount = result.bookings?.length || booking.totalInSeries;
        setSuccess(
          `Successfully cancelled ${cancelledCount} bookings in the series.`
        );
        setShowCancelSeriesConfirm(false);
        fetchBookingData();
      } else {
        setError(result?.message || "Failed to cancel series");
      }
    } catch (err) {
      console.error("Error canceling series:", err);
      setError(err.message || "Failed to cancel series");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedTime) {
      setError("Please select a new time");
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation ReschedulePublicBooking($input: ReschedulePublicBookingInput!) {
            reschedulePublicBooking(input: $input) {
              success
              message
              booking {
                id
                date
                timeStart
                timeEnd
              }
            }
          }
        `,
        variables: {
          input: {
            token,
            newDate: selectedDate.format("YYYY-MM-DD"),
            newTimeStart: selectedTime.timeStart,
            newTimeEnd: selectedTime.timeEnd,
          },
        },
      });

      const result =
        response.data?.data?.reschedulePublicBooking ||
        response.data?.reschedulePublicBooking;

      if (result?.success) {
        setSuccess("Your booking has been rescheduled successfully.");
        setShowReschedule(false);
        setSelectedTime(null);
        fetchBookingData();
      } else {
        setError(result?.message || "Failed to reschedule booking");
      }
    } catch (err) {
      console.error("Error rescheduling booking:", err);
      setError(err.message || "Failed to reschedule booking");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return dayjs(dateStr).format("dddd, MMMM D, YYYY");
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    // Handle both 24h and 12h formats
    if (
      timeStr.includes("am") ||
      timeStr.includes("pm") ||
      timeStr.includes("AM") ||
      timeStr.includes("PM")
    ) {
      return timeStr;
    }
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { bg: "#E8F5E9", color: "#4CAF50", text: "Confirmed" },
      pending: { bg: "#FFF8E1", color: "#FFA000", text: "Pending" },
      canceled: { bg: "#FFEBEE", color: "#F44336", text: "Canceled" },
      rescheduled: { bg: "#E3F2FD", color: "#2196F3", text: "Rescheduled" },
    };
    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span
        style={{
          backgroundColor: config.bg,
          color: config.color,
          padding: "6px 16px",
          borderRadius: "20px",
          fontSize: "14px",
          fontWeight: "600",
        }}
      >
        {config.text}
      </span>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={{ marginTop: "16px", color: "#666" }}>
              Loading booking details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state (no booking found)
  if (error && !booking) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorContainer}>
            <AlertCircle size={48} color="#F44336" />
            <h2 style={{ marginTop: "16px", color: "#333" }}>
              Unable to Load Booking
            </h2>
            <p style={{ color: "#666" }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const isCanceled = booking?.status === "canceled";

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Manage Your Booking</h1>
          {booking && getStatusBadge(booking.status)}
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div style={styles.successMessage}>
            <CheckCircle size={20} />
            <span>{success}</span>
          </div>
        )}
        {error && (
          <div style={styles.errorMessage}>
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* Booking Details */}
        {booking && (
          <div style={styles.detailsSection}>
            <h2 style={styles.sectionTitle}>
              <Calendar size={20} style={{ marginRight: "8px" }} />
              {eventType?.title || "Booking Details"}
            </h2>

            <div style={styles.detailsGrid}>
              <div style={styles.detailItem}>
                <Calendar size={18} color="#2196F3" />
                <div>
                  <span style={styles.detailLabel}>Date</span>
                  <span style={styles.detailValue}>
                    {formatDate(booking.date)}
                  </span>
                </div>
              </div>

              <div style={styles.detailItem}>
                <Clock size={18} color="#2196F3" />
                <div>
                  <span style={styles.detailLabel}>Time</span>
                  <span style={styles.detailValue}>
                    {formatTime(booking.timeStart)} -{" "}
                    {formatTime(booking.timeEnd)}
                  </span>
                </div>
              </div>

              <div style={styles.detailItem}>
                <User size={18} color="#2196F3" />
                <div>
                  <span style={styles.detailLabel}>Name</span>
                  <span style={styles.detailValue}>{booking.name}</span>
                </div>
              </div>

              <div style={styles.detailItem}>
                <Mail size={18} color="#2196F3" />
                <div>
                  <span style={styles.detailLabel}>Email</span>
                  <span style={styles.detailValue}>{booking.email}</span>
                </div>
              </div>

              {booking.assignedHostName && (
                <div style={styles.detailItem}>
                  <User size={18} color="#4CAF50" />
                  <div>
                    <span style={styles.detailLabel}>Host</span>
                    <span style={styles.detailValue}>
                      {booking.assignedHostName}
                    </span>
                  </div>
                </div>
              )}

              {booking.notes && (
                <div style={{ ...styles.detailItem, gridColumn: "1 / -1" }}>
                  <div>
                    <span style={styles.detailLabel}>Notes</span>
                    <span style={styles.detailValue}>{booking.notes}</span>
                  </div>
                </div>
              )}

              {/* Recurring Series Info */}
              {booking.recurringSeriesId && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    marginTop: "16px",
                    padding: "16px",
                    backgroundColor: "#f0f9ff",
                    borderRadius: "8px",
                    border: "1px solid #0ea5e9",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <span style={{ fontSize: "18px" }}>🔄</span>
                    <span style={{ fontWeight: "600", color: "#0369a1" }}>
                      Recurring Series
                    </span>
                  </div>
                  <p style={{ fontSize: "14px", color: "#334155", margin: 0 }}>
                    This is booking <strong>{booking.recurringIndex}</strong> of{" "}
                    <strong>{booking.totalInSeries}</strong> in a recurring
                    series.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        {!isCanceled && (
          <div style={styles.actionsSection}>
            <h2 style={styles.sectionTitle}>Actions</h2>

            <div style={styles.actionButtons}>
              <button
                style={styles.rescheduleButton}
                onClick={() => {
                  setShowReschedule(!showReschedule);
                  setShowCancelConfirm(false);
                }}
              >
                <RefreshCw size={18} />
                Reschedule Booking
                {showReschedule ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </button>

              <button
                style={styles.cancelButton}
                onClick={() => {
                  setShowCancelConfirm(!showCancelConfirm);
                  setShowReschedule(false);
                  setShowCancelSeriesConfirm(false);
                }}
              >
                <XCircle size={18} />
                Cancel Booking
              </button>

              {/* Cancel Entire Series button - only show for recurring bookings */}
              {booking?.recurringSeriesId && (
                <button
                  style={{
                    ...styles.cancelButton,
                    backgroundColor: "#7c3aed",
                    marginLeft: "8px",
                  }}
                  onClick={() => {
                    setShowCancelSeriesConfirm(!showCancelSeriesConfirm);
                    setShowCancelConfirm(false);
                    setShowReschedule(false);
                  }}
                >
                  <XCircle size={18} />
                  Cancel Entire Series ({booking.totalInSeries} bookings)
                </button>
              )}
            </div>

            {/* Cancel Confirmation */}
            {showCancelConfirm && (
              <div style={styles.confirmBox}>
                <h3 style={{ margin: "0 0 12px 0", color: "#D32F2F" }}>
                  Are you sure you want to cancel?
                </h3>
                <p style={{ margin: "0 0 16px 0", color: "#666" }}>
                  This action cannot be undone. The organizer will be notified
                  of your cancellation.
                </p>
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    style={styles.confirmCancelBtn}
                    onClick={handleCancel}
                    disabled={actionLoading}
                  >
                    {actionLoading ? "Canceling..." : "Yes, Cancel Booking"}
                  </button>
                  <button
                    style={styles.nevermindBtn}
                    onClick={() => setShowCancelConfirm(false)}
                    disabled={actionLoading}
                  >
                    No, Keep It
                  </button>
                </div>
              </div>
            )}

            {/* Cancel Series Confirmation */}
            {showCancelSeriesConfirm && booking?.recurringSeriesId && (
              <div
                style={{
                  ...styles.confirmBox,
                  backgroundColor: "#faf5ff",
                  borderColor: "#7c3aed",
                }}
              >
                <h3 style={{ margin: "0 0 12px 0", color: "#7c3aed" }}>
                  Cancel Entire Recurring Series?
                </h3>
                <p style={{ margin: "0 0 16px 0", color: "#666" }}>
                  This will cancel{" "}
                  <strong>all {booking.totalInSeries} bookings</strong> in this
                  recurring series. This action cannot be undone.
                </p>
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    style={{
                      ...styles.confirmCancelBtn,
                      backgroundColor: "#7c3aed",
                    }}
                    onClick={handleCancelSeries}
                    disabled={actionLoading}
                  >
                    {actionLoading
                      ? "Canceling..."
                      : `Yes, Cancel All ${booking.totalInSeries} Bookings`}
                  </button>
                  <button
                    style={styles.nevermindBtn}
                    onClick={() => setShowCancelSeriesConfirm(false)}
                    disabled={actionLoading}
                  >
                    No, Keep Them
                  </button>
                </div>
              </div>
            )}

            {/* Reschedule Panel with Full Calendar */}
            {showReschedule && (
              <div style={styles.rescheduleBox}>
                <h3 style={{ margin: "0 0 16px 0", color: "#1565C0" }}>
                  Select a New Date & Time
                </h3>

                {availabilityData ? (
                  <div style={styles.rescheduleContent}>
                    {/* Calendar and Time Slots side by side */}
                    <div style={styles.calendarTimeContainer}>
                      {renderMonthView()}
                      {renderTimeSlots()}
                    </div>

                    {/* Confirm Reschedule */}
                    {selectedTime && (
                      <div style={styles.confirmRescheduleSection}>
                        <p style={styles.selectedSummary}>
                          <strong>New booking:</strong>{" "}
                          {selectedDate.format("dddd, MMMM D, YYYY")} at{" "}
                          {selectedTime.timeStart}
                        </p>
                        <button
                          style={styles.confirmRescheduleBtn}
                          onClick={handleReschedule}
                          disabled={actionLoading}
                        >
                          {actionLoading
                            ? "Rescheduling..."
                            : "Confirm Reschedule"}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ color: "#666" }}>
                    No availability data found. Please contact the organizer to
                    reschedule.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Canceled message */}
        {isCanceled && (
          <div style={styles.canceledMessage}>
            <XCircle size={24} color="#F44336" />
            <p>
              This booking has been canceled. If you'd like to book again,
              please visit the booking page.
            </p>
          </div>
        )}

        {/* Footer */}
        <div style={styles.footer}>
          <p style={{ margin: 0, color: "#888", fontSize: "14px" }}>
            Powered by InstaConnect
          </p>
        </div>
      </div>

      {/* Spinner CSS */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Styles
const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#F5F7FA",
    padding: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    paddingTop: "40px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    maxWidth: "850px",
    width: "100%",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #2196F3 0%, #00BCD4 100%)",
    padding: "30px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    color: "#fff",
    fontSize: "24px",
    fontWeight: "600",
    margin: 0,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #E3F2FD",
    borderTop: "4px solid #2196F3",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    textAlign: "center",
  },
  successMessage: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "#E8F5E9",
    color: "#2E7D32",
    padding: "12px 20px",
    margin: "20px",
    borderRadius: "8px",
    fontSize: "14px",
  },
  errorMessage: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "#FFEBEE",
    color: "#C62828",
    padding: "12px 20px",
    margin: "20px",
    borderRadius: "8px",
    fontSize: "14px",
  },
  detailsSection: {
    padding: "20px 30px",
    borderBottom: "1px solid #eee",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    margin: "0 0 20px 0",
    display: "flex",
    alignItems: "center",
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px",
  },
  detailItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "12px",
    backgroundColor: "#F9FAFB",
    borderRadius: "8px",
  },
  detailLabel: {
    display: "block",
    fontSize: "12px",
    color: "#888",
    marginBottom: "4px",
  },
  detailValue: {
    display: "block",
    fontSize: "15px",
    color: "#333",
    fontWeight: "500",
  },
  actionsSection: {
    padding: "20px 30px",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  rescheduleButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    backgroundColor: "#E3F2FD",
    color: "#1565C0",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  cancelButton: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    backgroundColor: "#FFEBEE",
    color: "#C62828",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  confirmBox: {
    marginTop: "20px",
    padding: "20px",
    backgroundColor: "#FFF8E1",
    borderRadius: "8px",
    borderLeft: "4px solid #FFA000",
  },
  confirmCancelBtn: {
    padding: "10px 20px",
    backgroundColor: "#D32F2F",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  nevermindBtn: {
    padding: "10px 20px",
    backgroundColor: "#fff",
    color: "#666",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  rescheduleBox: {
    marginTop: "20px",
    padding: "20px",
    backgroundColor: "#F5F7FA",
    borderRadius: "12px",
    border: "1px solid #E0E0E0",
  },
  rescheduleContent: {},
  calendarTimeContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  // Calendar styles
  calendarContainer: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "16px",
    border: "1px solid #E0E0E0",
  },
  monthHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  monthTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    margin: 0,
  },
  navButton: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F5F5F5",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#555",
    transition: "all 0.2s",
  },
  weekdaysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    marginBottom: "8px",
  },
  weekday: {
    textAlign: "center",
    fontSize: "12px",
    fontWeight: "600",
    color: "#888",
    padding: "8px 0",
  },
  daysGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
  },
  dayCell: {
    aspectRatio: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dayButton: {
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    color: "#333",
    transition: "all 0.2s",
  },
  dayButtonSelected: {
    backgroundColor: "#2196F3",
    color: "#fff",
  },
  dayButtonToday: {
    border: "2px solid #2196F3",
  },
  dayButtonUnavailable: {
    color: "#ccc",
    cursor: "not-allowed",
  },
  // Time slots styles
  timeSlotsContainer: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "16px",
    border: "1px solid #E0E0E0",
  },
  timeSlotsTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    margin: "0 0 8px 0",
    display: "flex",
    alignItems: "center",
  },
  selectedDateText: {
    fontSize: "14px",
    color: "#666",
    margin: "0 0 16px 0",
  },
  timeSlotsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "8px",
    maxHeight: "280px",
    overflowY: "auto",
  },
  timeSlotButton: {
    padding: "10px 12px",
    backgroundColor: "#F5F5F5",
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#333",
    transition: "all 0.2s",
    textAlign: "center",
  },
  timeSlotButtonSelected: {
    backgroundColor: "#2196F3",
    color: "#fff",
    borderColor: "#2196F3",
  },
  noSlotsMessage: {
    textAlign: "center",
    padding: "30px 20px",
    color: "#666",
  },
  confirmRescheduleSection: {
    marginTop: "20px",
    padding: "16px",
    backgroundColor: "#E8F5E9",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
  },
  selectedSummary: {
    margin: 0,
    color: "#2E7D32",
    fontSize: "14px",
  },
  confirmRescheduleBtn: {
    padding: "12px 24px",
    backgroundColor: "#4CAF50",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  canceledMessage: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "20px 30px",
    backgroundColor: "#FFEBEE",
    margin: "20px 30px",
    borderRadius: "8px",
  },
  footer: {
    padding: "20px 30px",
    borderTop: "1px solid #eee",
    textAlign: "center",
  },
};

export default ManageBooking;
